import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { NODE_API_BASE_URL } from "../config";
import { authHeaders } from "../cms/utils/session";
import {
  ChartCard, TrendArea, RankedBars, GroupedBars, DataTable,
  StatChip, ChipRow, PageHead, FieldSelect, ToolRow,
  inr, num, numOrNA, SERIES, ACCENT, LINE,
} from "./chartKit";

/**
 * Revenue and usage reporting.
 *
 * Its own endpoint and its own period control, separate from the dashboard's
 * headline payload: these aggregates are heavier to compute and are read on
 * purpose rather than glanced at, so making the whole dashboard wait for them
 * would be the wrong trade.
 *
 * Every card checks its own slice of the response and stands down if the backend
 * is not sending it yet, so the section ships before the API is complete and
 * each report appears the day its data does.
 */

const GRANULARITY = [
  { key: "monthly", label: "Monthly" },
  { key: "annual", label: "Annually" },
];

const TYPE_LABEL = { attorney: "Attorney / IP", corporate: "Corporate" };

/* Attorney and corporate keep the same two hues they wear everywhere else on
   this screen. Colour follows the entity, not the row it happens to be in. */
const TYPE_COLOR = { attorney: SERIES[0], corporate: SERIES[1] };

const arr = (v) => (Array.isArray(v) ? v : []);

const lastDay = (y, m) => new Date(y, m, 0).getDate();

/**
 * The date range one point on a period chart stands for.
 *
 * Taken from the row when the API states it, which is the only version that is
 * certainly right — it knows where it cut each bucket. Failing that, the raw
 * `period` key is read, since "2026-04" and "2026" are unambiguous. The
 * displayed label is never parsed: it is written for people, and reading
 * "Apr 2026" back into a date is how a report starts disagreeing with the list
 * it links to.
 */
const periodRange = (row) => {
  if (row?.period_start && row?.period_end) {
    return { fromDate: row.period_start, toDate: row.period_end };
  }
  const key = String(row?.period ?? "");
  const month = key.match(/^(\d{4})-(\d{2})$/);
  if (month) {
    const [, y, m] = month;
    return { fromDate: `${y}-${m}-01`, toDate: `${y}-${m}-${lastDay(Number(y), Number(m))}` };
  }
  const year = key.match(/^(\d{4})$/);
  if (year) return { fromDate: `${year[1]}-01-01`, toDate: `${year[1]}-12-31` };
  return null;
};

const Failed = ({ onRetry }) => (
  <div style={{
    padding: "28px 20px", textAlign: "center", background: "#FFF7F2",
    border: "1px dashed #FBD3B4", borderRadius: 14, color: "#9a3412", fontSize: 13,
  }}>
    Reports could not be loaded.
    <button
      onClick={onRetry}
      style={{
        marginLeft: 10, background: "#fff", border: "1px solid #f3c6b5", color: "#9a3412",
        borderRadius: 7, padding: "3px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
      }}
    >
      Try again
    </button>
  </div>
);

const Loading = () => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} style={{
        height: 260, background: "#fff", borderRadius: 16, border: `1px solid ${LINE}`,
        padding: "20px 22px",
      }}>
        <div style={{ width: 34, height: 3, borderRadius: 2, background: "#EEEDF5", marginBottom: 10 }} />
        <div style={{ width: "40%", height: 14, borderRadius: 6, background: "#F2F1F8", marginBottom: 18 }} />
        <div style={{ height: 170, borderRadius: 10, background: "linear-gradient(90deg,#F7F6FB 25%,#EFEDF7 50%,#F7F6FB 75%)", backgroundSize: "200% 100%", animation: "dbShimmer 1.4s infinite" }} />
      </div>
    ))}
  </div>
);

export default function RevenueReports({ view, dates }) {
  const navigate = useNavigate();
  const [grain, setGrain] = useState("monthly");
  const [state, setState] = useState({ data: null, loading: true, error: false });

  const from = dates?.[0] ? dates[0].format("YYYY-MM-DD") : "";
  const to = dates?.[1] ? dates[1].format("YYYY-MM-DD") : "";

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: false }));
    try {
      const res = await axios.get(`${NODE_API_BASE_URL}/api/dashboard/reports`, {
        params: {
          granularity: grain,
          ...(from && to ? { from_date: from, to_date: to } : {}),
        },
        headers: { ...authHeaders() },
      });
      setState({ data: res.data?.data ?? null, loading: false, error: false });
    } catch {
      setState({ data: null, loading: false, error: true });
    }
  }, [grain, from, to]);

  useEffect(() => { load(); }, [load]);

  const d = state.data || {};
  /* `period`, `period_start` and `period_end` are kept rather than reduced to a
     label: they are what a click on this point filters the payment list by. */
  const series = arr(d.revenue_timeseries).map((r) => ({
    label: r.label || r.period,
    period: r.period,
    period_start: r.period_start,
    period_end: r.period_end,
    amount: Number(r.amount || 0),
    orders: Number(r.orders || 0),
  }));
  const byType = arr(d.revenue_by_user_type).map((r) => ({
    label: r.label || r.period,
    period: r.period,
    period_start: r.period_start,
    period_end: r.period_end,
    attorney: Number(r.attorney || 0),
    corporate: Number(r.corporate || 0),
  }));
  const typeTotal = d.revenue_by_user_type_total || {};
  const byClient = arr(d.revenue_by_client);
  const topAttorney = arr(d.top_customers?.attorney);
  const topCorporate = arr(d.top_customers?.corporate);
  const usage = arr(d.trademark_usage);
  const planRevenue = arr(d.plan_revenue);

  /* `extra` lets a caller name the panel to land on, which the 360 view reads
     as `openPanel` — a chart about trademark counts should open the breakdown
     those counts came from, not the overview. */
  const openCustomer = (row, extra = {}) => row?.user_id
    && navigate(`/cms/manageUserData/user-360-view/${row.user_id}`, {
      state: { userName: row.customer_name, userEmail: row.customer_email, ...extra },
    });

  /* The payments that add up to a point on a period chart. A period with no
     range to resolve does not navigate — opening the whole list would claim a
     filter that was never applied. */
  const openPeriod = (row, extra = {}) => {
    const range = periodRange(row);
    if (range) navigate("/cms/payment-list", { state: { ...range, ...extra } });
  };

  const header = (
    <>
      {/* The eyebrow names the section and the title names this report, so the
          two must not be the same words — that just reads as the heading
          printed twice. */}
      <PageHead
        eyebrow="Reports"
        title="Revenue reports"
        description={`${from && to ? `${from} to ${to}` : "All time"} — by period, user type, client and plan.`}
      />
      <ToolRow>
        <FieldSelect label="Period" value={grain} onChange={setGrain} options={GRANULARITY} />
      </ToolRow>
    </>
  );

  if (state.loading) return <div style={{ marginBottom: 20 }}>{header}<Loading /></div>;
  if (state.error) return <div style={{ marginBottom: 20 }}>{header}<Failed onRetry={load} /></div>;

  const grainLabel = grain === "annual" ? "year" : "month";
  /* A report with no rows is hidden rather than shown as an empty box. Six
     "nothing to plot" panels is a page that looks broken; the sections that do
     have data read better on their own, and a report reappears the moment its
     data does. */
  const nothingAtAll = ![series, byType, byClient, topAttorney, topCorporate, usage, planRevenue]
    .some((a) => a.length);

  const best = series.length
    ? series.reduce((a, b) => (b.amount > a.amount ? b : a), series[0])
    : null;

  // ── Cards view: the same reports as figures and tables, no plots ───────────
  if (nothingAtAll) {
    return (
      <div style={{ marginBottom: 20 }}>
        {header}
        <div style={{
          padding: "44px 20px", textAlign: "center", background: "#fff",
          border: `1px dashed ${LINE}`, borderRadius: 16, color: "#9C97B2", fontSize: 13,
        }}>
          No revenue recorded for this period.
        </div>
      </div>
    );
  }

  if (view === "cards") {
    return (
      <div style={{ marginBottom: 20 }}>
        {header}

        <ChipRow>
          <StatChip
            value={inr(typeTotal.attorney)} label="Attorney / IP revenue"
            delta={typeTotal.attorney_customers != null ? `${num(typeTotal.attorney_customers)} customers` : null}
          />
          <StatChip
            value={inr(typeTotal.corporate)} label="Corporate revenue"
            delta={typeTotal.corporate_customers != null ? `${num(typeTotal.corporate_customers)} customers` : null}
          />
          <StatChip
            value={series.length ? inr(Math.max(...series.map((s) => s.amount))) : "—"}
            label={`Best ${grainLabel}`}
            delta={best ? best.label : null}
          />
        </ChipRow>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <ChartCard title={`Revenue by ${grainLabel}`}>
            <DataTable
              rows={[...series].reverse()}
              columns={[
                { key: "label", title: grainLabel },
                { key: "orders", title: "Orders", align: "right", render: (r) => num(r.orders) },
                { key: "amount", title: "Collected", align: "right", render: (r) => inr(r.amount) },
              ]}
            />
          </ChartCard>

          <ChartCard title="Revenue by client" hint="highest first">
            <DataTable
              rows={byClient}
              columns={[
                { key: "customer_name", title: "Client" },
                { key: "user_type", title: "Type", render: (r) => TYPE_LABEL[r.user_type] || r.user_type || "—" },
                { key: "orders", title: "Orders", align: "right", render: (r) => num(r.orders) },
                { key: "revenue", title: "Revenue", align: "right", render: (r) => inr(r.revenue) },
              ]}
            />
          </ChartCard>

          <ChartCard title="Top customers" hint="attorney and corporate, side by side">
            <DataTable
              rows={[...topAttorney, ...topCorporate]
                .sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0))}
              columns={[
                { key: "customer_name", title: "Customer" },
                { key: "user_type", title: "Type", render: (r) => TYPE_LABEL[r.user_type] || r.user_type || "—" },
                { key: "revenue", title: "Revenue", align: "right", render: (r) => inr(r.revenue) },
              ]}
            />
          </ChartCard>

          <ChartCard title="Highest trademark usage" hint="subscribed vs actually monitored">
            <DataTable
              rows={usage}
              onRowClick={(row) => openCustomer(row, { openPanel: "tm-breakdown" })}
              columns={[
                { key: "customer_name", title: "Customer" },
                { key: "plan_name", title: "Plan", render: (r) => (r.plan_name || "").replace(" Plan", "") || "—" },
                { key: "total_tm_count", title: "Subscribed", align: "right", render: (r) => numOrNA(r.total_tm_count) },
                { key: "live_tm_count", title: "In portfolio", align: "right", render: (r) => numOrNA(r.live_tm_count) },
              ]}
            />
          </ChartCard>
        </div>
      </div>
    );
  }

  // ── Charts view ───────────────────────────────────────────────────────────
  return (
    <div style={{ marginBottom: 20 }}>
      {header}

      {series.length > 0 && (
        <ChartCard
          title={`Revenue by ${grainLabel}`}
          hint={`gross collected \u00b7 click a ${grainLabel} to open its payments`}
          accent={ACCENT.purple}
          style={{ marginBottom: 18 }}
        >
          <TrendArea data={series} height={360} onPointClick={openPeriod} />
        </ChartCard>
      )}

      {(byType.length > 0 || typeTotal.attorney != null || typeTotal.corporate != null) && (
      <ChartCard
        title="Revenue by user type"
        hint={`attorney against corporate, per ${grainLabel} \u00b7 click a bar to open its payments`}
        accent={ACCENT.blue}
        style={{ marginBottom: 18 }}
      >
        <ChipRow>
          <StatChip
            value={inr(typeTotal.attorney)} label="Attorney / IP"
            delta={typeTotal.attorney_customers != null ? `${num(typeTotal.attorney_customers)} customers` : null}
          />
          <StatChip
            value={inr(typeTotal.corporate)} label="Corporate"
            delta={typeTotal.corporate_customers != null ? `${num(typeTotal.corporate_customers)} customers` : null}
          />
        </ChipRow>
        {/* Grouped, not two y-axes: both series are rupees on one scale, which is
            the only honest way to put them on the same plot. */}
        <GroupedBars
          data={byType}
          series={[
            { key: "attorney", label: TYPE_LABEL.attorney },
            { key: "corporate", label: TYPE_LABEL.corporate },
          ]}
          height={300}
          /* Both halves of what the bar means: its period, and which of the two
             user types it belongs to. */
          onBarClick={(key, row) => openPeriod(row, { userType: key })}
        />
      </ChartCard>
      )}

      {/* Two facets rather than one chart coloured by type: the comparison being
          made is within each group, and facets keep every bar in slot-1 ink
          instead of asking the reader to match hues down a long list. */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
        {topAttorney.length > 0 && (
        <ChartCard title="Top attorneys" hint="by revenue" accent={ACCENT.purple}>
          <RankedBars
            data={topAttorney.map((r) => ({ ...r, name: r.customer_name }))}
            valueKey="revenue"
            nameWidth={128}
            color={TYPE_COLOR.attorney}
            onBarClick={(b) => openCustomer(b?.payload)}
            extra={(p) => (p?.orders ? `${num(p.orders)} orders` : "")}
          />
        </ChartCard>
        )}

        {topCorporate.length > 0 && (
        <ChartCard title="Top corporates" hint="by revenue" accent={ACCENT.orange}>
          <RankedBars
            data={topCorporate.map((r) => ({ ...r, name: r.customer_name }))}
            valueKey="revenue"
            nameWidth={128}
            color={TYPE_COLOR.corporate}
            onBarClick={(b) => openCustomer(b?.payload)}
            extra={(p) => (p?.orders ? `${num(p.orders)} orders` : "")}
          />
        </ChartCard>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
        {byClient.length > 0 && (
        <ChartCard title="Revenue by client" hint="highest first · click a bar to open the customer" accent={ACCENT.teal}>
          <RankedBars
            data={byClient.map((r) => ({ ...r, name: r.customer_name }))}
            valueKey="revenue"
            nameWidth={132}
            onBarClick={(b) => openCustomer(b?.payload)}
            extra={(p) => [
              TYPE_LABEL[p?.user_type] || p?.user_type,
              p?.orders ? `${num(p.orders)} orders` : null,
            ].filter(Boolean).join(" · ")}
          />
        </ChartCard>
        )}

        {planRevenue.length > 0 && (
        <ChartCard title="Revenue by plan" hint="collected per plan" accent={ACCENT.green}>
          <RankedBars
            data={planRevenue.map((r) => ({ ...r, name: (r.plan_name || "").replace(" Plan", "") }))}
            valueKey="revenue"
            nameWidth={106}
            extra={(p) => (p?.subscriptions ? `${num(p.subscriptions)} subscriptions` : "")}
          />
        </ChartCard>
        )}
      </div>

      {usage.length > 0 && (
      <ChartCard
        title="Highest trademark usage"
        hint="what the plan covers against what is actually in the portfolio · click a bar to open the breakdown"
        accent={ACCENT.amber}
      >
        <GroupedBars
            data={usage.map((r) => ({ ...r, name: r.customer_name }))}
            xKey="name"
            horizontal
            nameWidth={132}
            fmt={num}
            height={Math.max(220, usage.length * 44 + 40)}
            series={[
              { key: "total_tm_count", label: "Subscribed" },
              { key: "live_tm_count", label: "In portfolio" },
            ]}
            /* Both bars belong to one customer, so which of the two was hit
               changes nothing about where this goes — the series key is
               ignored on purpose. */
            onBarClick={(_key, row) => openCustomer(row, { openPanel: "tm-breakdown" })}
        />
      </ChartCard>
      )}
    </div>
  );
}
